import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { Col, Row } from 'antd';
import { omitBy } from 'lodash';

import FormItem, { TYPE_INPUT } from 'components/FormItem';

import RefreshIcon from 'resources/svg/refresh_icon.svg';

import { useChangeAddress } from 'hooks/useChangeAddress';
import { ORDERS } from 'utils';
import { NFTParamsTypes } from 'pages/nft-management';
import LENGTH_CONSTANTS from 'constants/length';
import { NFT_MANAGEMENT_FIELD } from 'constants/nft';
import { ROLE, ROLE_STATUS } from 'constants/common';

const { KEYWORD, PAGE } = NFT_MANAGEMENT_FIELD;
const { DEFAULT_PAGE } = LENGTH_CONSTANTS;
const { FIELD, ORDER } = ORDERS;

type SearchFormProps = {
  onSubmit: (values: any) => void;
  params: NFTParamsTypes;
};

const initialValues = {
  keyword: '',
  status: '',
  role: '',
};

const SearchForm = ({ onSubmit, params }: SearchFormProps) => {
  const { t } = useTranslation();
  const formRef = useRef<any>(null);

  const formatRoleOptions = [
    {
      name: t('admin_management.txt_all_role'),
      value: '',
    },
    {
      name: t('admin_management.txt_role_creater'),
      value: ROLE.CREATOR_ADMIN.value,
    },
    {
      name: t('admin_management.txt_sale_admin'),
      value: ROLE.SALE_ADMIN.value,
    },
    {
      name: t('admin_management.txt_role_super'),
      value: ROLE.SUPER_ADMIN.value,
    },
  ];

  const formatStatusOptions = [
    {
      name: 'All status',
      value: '',
    },
    {
      name: 'Inactive',
      value: ROLE_STATUS.INACTIVE.value,
    },
    {
      name: 'Active',
      value: ROLE_STATUS.ACTIVE.value,
    },
  ];
  const handleChangeField =
    (setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void, field: string) =>
    (value: string) => {
      setFieldValue(field, value?.trim());
      onSubmit({
        ...params,
        [field]: value?.trim(),
        [PAGE]: DEFAULT_PAGE,
      });
    };

  const handleResetForm = () => {
    formRef?.current?.resetForm();
    onSubmit({
      ...omitBy(params, [FIELD, ORDER]),
      ...initialValues,
      [PAGE]: DEFAULT_PAGE,
    });
  };

  useChangeAddress({ callback: handleResetForm });

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} innerRef={formRef}>
      {({ setFieldValue }) => (
        <Form className="search-form nft-management-search-form">
          <Row gutter={20} className="nft-management-search-form__row">
            <Col lg={8} sm={12} xs={24}>
              <FormItem
                name={KEYWORD}
                className="search-form__name--input"
                placeholder={t('admin_management.txt_search_placeholder')}
                onSearch={handleChangeField(setFieldValue, KEYWORD)}
                typeInput={TYPE_INPUT.SEARCH}
              />
            </Col>
            <Col lg={3} sm={12} xs={24}>
              <FormItem
                options={formatRoleOptions}
                name="role"
                onChange={handleChangeField(setFieldValue, 'role')}
                typeInput={TYPE_INPUT.SELECT}
                defaultValue=""
                allowClear
              />
            </Col>
            <Col lg={3} sm={12} xs={24}>
              <FormItem
                options={formatStatusOptions}
                name="status"
                onChange={handleChangeField(setFieldValue, 'status')}
                typeInput={TYPE_INPUT.SELECT}
                defaultValue=""
                allowClear
              />
            </Col>
            <Col lg={10} sm={24} xs={24} className="nft-management-search-form__group-button">
              <img src={RefreshIcon} onClick={handleResetForm} className="cursor-pointer" />
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
};

export default SearchForm;
